import { useMemo } from "react";
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ProfileFormValues } from "./profileFormTypes";
import {
  AddressSearchWithDetails,
  type AddressDetailFieldPaths,
} from "@/components/common";

const REGISTERED_ADDRESS_FIELDS = {
  street: "registered_street",
  city: "registered_city",
  postalCode: "registered_postal_code",
  province: "registered_province",
  country: "registered_country",
} as const satisfies AddressDetailFieldPaths<ProfileFormValues>;

interface AddressFieldsProps {
  register: UseFormRegister<ProfileFormValues>;
  setValue: UseFormSetValue<ProfileFormValues>;
  watch: UseFormWatch<ProfileFormValues>;
  errors?: FieldErrors<ProfileFormValues>;
  syncKey: string;
}

export function AddressFields({
  register,
  setValue,
  watch,
  errors,
  syncKey,
}: AddressFieldsProps) {
  const { t } = useTranslation(["settings", "common"]);

  const labels = useMemo(
    () => ({
      search: t("common:addressSearch.label"),
      searchPlaceholder: t("common:addressSearch.placeholder"),
      editDetails: t("common:addressSearch.editDetails"),
      street: t("settings:forms.address.street"),
      streetPlaceholder: t("settings:forms.address.streetPh"),
      city: t("settings:forms.address.city"),
      cityPlaceholder: t("settings:forms.address.cityPh"),
      postalCode: t("settings:forms.address.postalCode"),
      postalPlaceholder: t("settings:forms.address.postalPh"),
      country: t("settings:forms.address.country"),
      countryPlaceholder: t("settings:forms.address.countryPh"),
      province: t("settings:forms.address.state"),
      provincePlaceholder: t("settings:forms.address.statePh"),
    }),
    [t],
  );

  return (
    <AddressSearchWithDetails
      fields={REGISTERED_ADDRESS_FIELDS}
      register={register}
      setValue={setValue}
      watch={watch}
      errors={errors}
      labels={labels}
      syncKey={syncKey}
      enableValidation
      requiredDetails
    />
  );
}
